/**
 * @module  experience/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';

// Interfaces
import { BrowseEntryConfigData } from '@core/aem/interfaces/common/browse-entry.interface';
import { PlanTypeEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/plan-type-entry-change.interface';

// Constants
import {
  BillingTypeEnum,
  SHARED_BROWSE_CONSTANTS,
  SHARED_BROWSE_COMPONENTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';

// Services
import { LoggerService } from '@core/logger/services/logger.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';

@Component({
  selector: 'tmo-plan-type',
  templateUrl: './plan-type.component.html',
})
export class PlanTypeComponent implements OnInit, OnDestroy {
  /**
   * Configurable AEM data passed from parent component
   */
  @Input() public planTypeConfigurableData: BrowseEntryConfigData;
  /** Event emitted when plan is selected
   * It emits the 'Event' object.
   */
  @Output() public onPlanSelect: EventEmitter<PlanTypeEntryChangeInterface>;
  /**
   * Configurable AEM data passed from parent component
   */
  @Input() public planType: string;
  /**
   * Holds boolean to check if it is prepaid only or postpaid only or both
   */
  @Input() public billingType: string;
  /**
   * variable to hold selectPlanType value
   */
  public selectedPlanType: string;
  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private logger: LoggerService,
    private sharedBrowseService: SharedBrowseService
  ) {
    this.onPlanSelect = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Event handler for radio button selection
   */
  public selectPlanType(payValue: string, showCreditStatus: boolean): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.PLAN_TYPE,
      ` Entering selectPlanType() in ${SHARED_BROWSE_COMPONENTS.PLAN_TYPE}`,
      `. Input: payValue: ${payValue}`,
      `and showCreditStatus ${showCreditStatus}`
    );
    if (this.billingType === null) {
      const selectedCreditLevel = BillingTypeEnum[payValue];
      this.selectedPlanType =
        payValue === SHARED_BROWSE_CONSTANTS.prepaidAPIMap
          ? SHARED_BROWSE_CONSTANTS.prepaidAPIMap.toLocaleLowerCase()
          : SHARED_BROWSE_CONSTANTS.postpaidAPIMap.toLocaleLowerCase();
      this.onPlanSelect.emit({
        selectedPlanType: selectedCreditLevel,
        showCreditStatus: showCreditStatus,
      });
    }
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.PLAN_TYPE,
      ' Exiting selectPlanType() in ' + SHARED_BROWSE_COMPONENTS.PLAN_TYPE
    );
  }
  /**
   * Set default value of radio button
   */
  public ngOnInit(): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.PLAN_TYPE,
      ' Entering ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.PLAN_TYPE
    );

    this.selectedPlanType =
      this.planType || SHARED_BROWSE_CONSTANTS.postpaidAPIMap.toLocaleLowerCase();
    this.sharedBrowseService.zipCodeNotAvailableSource
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((value: boolean) => {
        if (value) {
          this.selectedPlanType =
            this.planType || SHARED_BROWSE_CONSTANTS.postpaidAPIMap.toLocaleLowerCase();
        }
      });
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.PLAN_TYPE,
      ' Exiting ngOnInit() in ' + SHARED_BROWSE_COMPONENTS.PLAN_TYPE
    );
  }
  /**
   * Method used to destroy data
   */
  public ngOnDestroy(): void {
    this.logger.debug(
      SHARED_BROWSE_CONSTANTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.PLAN_TYPE,
      ' entering and existing ngOnDestroy()in ' + SHARED_BROWSE_COMPONENTS.PLAN_TYPE
    );
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
